import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, Download, Loader2, AlertCircle } from 'lucide-react';
import { useTranslation } from '../context/LanguageContext';
import { useNotification } from '../context/NotificationContext';

const API_BASE = '/api';

const apiFetch = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = { ...options.headers };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...options, headers });
  if (res.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }
  return res.json();
};

/**
 * M2 Tier Price 编辑器
 *
 * 管理 channel_product 的阶梯价（本地 channel_product_tier_price 表 → M2 API 同步）。
 *
 * Props:
 *   entityId    - entity ID（必需）
 *   viewOnly    - 只读模式
 *   storeId     - 店铺 ID（可选，用于动态获取客户组列表）
 *   storeAuth   - { ... } M2 店铺认证（可选，提供后显示"从 M2 同步"按钮）
 *   channelSku  - M2 产品 SKU（可选）
 */
export default function M2TierPriceEditor({ entityId, viewOnly, storeId, storeAuth, channelSku, onPricesChange }) { const [customerGroups, setCustomerGroups] = useState([]); const [groupsLoaded, setGroupsLoaded] = useState(false);
  const { t } = useTranslation();
  const { notify } = useNotification();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  // ── 加载数据 ──
  useEffect(() => {
    if (!entityId) { setLoading(false); return; }
    setLoading(true);
    apiFetch(`${API_BASE}/channel/tier-prices?entity_id=${entityId}`)
      .then(res => {
        if (res.status === 'success' && res.data) {
          setItems(res.data);
        } else {
          setItems([]);
        }
      })
      .catch(err => {
        console.error('Failed to load tier prices:', err);
        notify(t('m2_tier_price.load_error'), 'error');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, [entityId]);

  // ── 加载客户组列表 ──
  useEffect(() => {
    if (!storeId) { setCustomerGroups([{ id: 0, code: 'NOT LOGGED IN' }, { id: 1, code: 'General' }]); setGroupsLoaded(true); return; }
    apiFetch(`${API_BASE}/channel/tier-prices/customer-groups?store_id=${storeId}`)
      .then(res => {
        if (res.status === 'success' && res.data && res.data.length > 0) {
          setCustomerGroups(res.data);
        } else {
          setCustomerGroups([{ id: 0, code: 'NOT LOGGED IN' }, { id: 1, code: 'General' }]);
        }
      })
      .catch(() => {
        setCustomerGroups([{ id: 0, code: 'NOT LOGGED IN' }, { id: 1, code: 'General' }]);
      })
      .finally(() => setGroupsLoaded(true));
  }, [storeId]);

  // ── 添加行 ──
  const handleAdd = () => {
    setItems([...items, {
      customer_group_id: 1,
      customer_group_code: 'General',
      qty: '10.0000',
      price: '0.000000',
      price_type: 'fixed',
      website_id: 0,
    }]);
  };

  // ── 删除行 ──
  const handleRemove = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // ── 更新字段 ──
  const handleFieldChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    // 自动更新 customer_group_code
    if (field === 'customer_group_id') {
      const groups = customerGroups.length > 0 ? customerGroups : [{ id: 0, code: 'NOT LOGGED IN' }, { id: 1, code: 'General' }];
      const group = groups.find(g => g.id === parseInt(value, 10));
      newItems[index].customer_group_code = group ? group.code : 'NOT LOGGED IN';
    }
    setItems(newItems);
  };

  // ── 保存 ──
  const handleSave = async () => {
    if (!entityId) {
      // 新建模式：通知父组件
      if (onPricesChange) onPricesChange(items);
      notify(t('m2_tier_price.save_success'), 'success');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        entity_id: entityId,
        items: items.map(item => ({
          customer_group_id: parseInt(item.customer_group_id, 10),
          customer_group_code: item.customer_group_code || 'NOT LOGGED IN',
          qty: parseFloat(item.qty) || 0,
          price: parseFloat(item.price) || 0,
          price_type: item.price_type || 'fixed',
          website_id: 0,
        })),
      };
      const res = await apiFetch(`${API_BASE}/channel/tier-prices/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 'success') {
        notify(t('m2_tier_price.save_success'), 'success');
      } else {
        notify(res.message || t('m2_tier_price.save_error'), 'error');
      }
    } catch (err) {
      notify(t('m2_tier_price.save_error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── 从 M2 同步 ──
  const handleSync = async () => {
    if (!entityId) return;
    setSyncing(true);
    try {
      const res = await apiFetch(`${API_BASE}/channel/tier-prices/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ entity_id: entityId }),
      });
      if (res.status === 'success') {
        notify(t('m2_tier_price.sync_success', { count: res.count }), 'success');
        // 刷新列表
        const listRes = await apiFetch(`${API_BASE}/channel/tier-prices?entity_id=${entityId}`);
        if (listRes.status === 'success' && listRes.data) {
          setItems(listRes.data);
        }
      } else {
        notify(res.message || t('m2_tier_price.sync_error'), 'error');
      }
    } catch (err) {
      notify(t('m2_tier_price.sync_error'), 'error');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8 text-slate-400">
        <Loader2 size={20} className="animate-spin mr-2" />
        {t('common.loading')}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* 工具栏 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {!viewOnly && (
            <button type="button" onClick={handleAdd}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30 dark:hover:bg-blue-900/50 rounded-lg transition-colors">
              <Plus size={14} />
              {t('m2_tier_price.add')}
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {storeAuth && channelSku && (
            <button type="button" onClick={handleSync} disabled={syncing}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30 dark:hover:bg-purple-900/50 rounded-lg transition-colors disabled:opacity-50">
              {syncing ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              {t('m2_tier_price.sync_from_m2')}
            </button>
          )}
          {!viewOnly && (
            <button type="button" onClick={handleSave} disabled={saving}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-emerald-600 bg-emerald-50 hover:bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/50 rounded-lg transition-colors disabled:opacity-50">
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {t('m2_tier_price.save')}
            </button>
          )}
        </div>
      </div>

      {/* 空状态 */}
      {items.length === 0 && !loading && (
        <div className="flex flex-col items-center justify-center py-8 text-slate-400 dark:text-slate-500">
          <AlertCircle size={32} className="mb-2 opacity-50" />
          <p className="text-sm">{t('m2_tier_price.no_data')}</p>
        </div>
      )}

      {/* 数据表格 */}
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400">
                <th className="px-2 py-1.5 text-left font-medium">{t('m2_tier_price.customer_group')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('m2_tier_price.qty')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('m2_tier_price.price')}</th>
                <th className="px-2 py-1.5 text-left font-medium">{t('m2_tier_price.price_type')}</th>
                {!viewOnly && <th className="px-2 py-1.5 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800/30">
                  <td className="px-2 py-1.5">
                    <select value={item.customer_group_id}
                      onChange={e => handleFieldChange(i, 'customer_group_id', e.target.value)}
                      disabled={viewOnly}
                      className="w-full bg-transparent border border-slate-200 dark:border-slate-600 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-400 disabled:opacity-60">
                      {(customerGroups.length > 0 ? customerGroups : [{ id: 0, code: 'NOT LOGGED IN' }, { id: 1, code: 'General' }]).map(g => (
                        <option key={g.id} value={g.id}>{g.code}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" step="0.0001" min="0"
                      value={item.qty}
                      onChange={e => handleFieldChange(i, 'qty', e.target.value)}
                      disabled={viewOnly}
                      className="w-20 bg-transparent border border-slate-200 dark:border-slate-600 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-400 disabled:opacity-60" />
                  </td>
                  <td className="px-2 py-1.5">
                    <input type="number" step="0.000001" min="0"
                      value={item.price}
                      onChange={e => handleFieldChange(i, 'price', e.target.value)}
                      disabled={viewOnly}
                      className="w-28 bg-transparent border border-slate-200 dark:border-slate-600 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-400 disabled:opacity-60" />
                  </td>
                  <td className="px-2 py-1.5">
                    <select value={item.price_type}
                      onChange={e => handleFieldChange(i, 'price_type', e.target.value)}
                      disabled={viewOnly}
                      className="bg-transparent border border-slate-200 dark:border-slate-600 rounded px-2 py-1 text-xs focus:ring-1 focus:ring-blue-400 disabled:opacity-60">
                      <option value="fixed">{t('m2_tier_price.fixed')}</option>
                      <option value="discount">{t('m2_tier_price.discount')}</option>
                    </select>
                  </td>
                  {!viewOnly && (
                    <td className="px-2 py-1.5">
                      <button type="button" onClick={() => handleRemove(i)}
                        className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
